import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import DocxPreviewPane from 'src/components/common/docxPreview/DocxPreviewPane';
import { displayToken, humanizeToken, IPreviewToken, PreviewTokenKind } from 'src/components/common/docxPreview/docxPreviewDom';
import { ensureStyles } from 'src/components/common/docxPreview/docxPreviewStyles';
import { previewApiFetch, previewErrorMessage } from 'src/components/common/docxPreview/previewApi';
import DocumentTemplate from 'src/models/DocumentTemplate';
import LandContractType from 'src/models/LandContractType';
import Lease from 'src/models/leasehold/Lease';
import { leasePreviewTarget } from './leasePreviewTargets';

export interface ILiveLeaseWindowHandle {
    /** The form changed; refresh once the user pauses. */
    notifyChange: () => void;
}

interface ILiveLeaseWindowProps {
    /** Opened by openLiveLeaseWindow() inside the click handler. */
    win: Window;
    clientNumber: number;
    projectNumber: number;
    title: string;
    /** The lease form as it stands, shaped the way Save sends it. */
    buildPayload: () => Lease;
    /** A value in the document was clicked. */
    onTokenClick: (token: string) => void;
    onClose: () => void;
}

interface IScreenWithOrigin extends Screen {
    availLeft?: number;
    availTop?: number;
}

type PreviewStatus = 'waiting' | 'updating' | 'ready' | 'error';

const WINDOW_NAME = 'landhawk-live-lease';
const PREVIEWABLE_TYPES = ['Lease Template', 'Memo', 'Order of Payment'];
/** Reference numbers 1-3 are the old Aethon documents the page still draws with react-pdf, not Word templates. */
const REACT_PDF_TEMPLATES = [1, 2, 3];
const POLL_MS = 1500;
const TYPING_PAUSE_MS = 450;

/**
 * Opens (or reuses) the Live Lease window. Call it directly from the click handler: browsers block pop-ups
 * that aren't opened by a user action.
 */
export function openLiveLeaseWindow(): Window {
    const screen = window.screen as IScreenWithOrigin;
    const width = Math.min(1000, Math.round(screen.availWidth * 0.5));
    const height = Math.round(screen.availHeight * 0.92);
    const left = (screen.availLeft || 0) + screen.availWidth - width;
    const top = screen.availTop || 0;

    return window.open('', WINDOW_NAME, `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`);
}

const LiveLeaseWindow = forwardRef<ILiveLeaseWindowHandle, ILiveLeaseWindowProps>((props, ref) => {
    const [container, setContainer] = useState<HTMLElement>(null);
    const [templates, setTemplates] = useState<DocumentTemplate[]>(undefined);
    const [templateId, setTemplateId] = useState<number>(undefined);
    const [docData, setDocData] = useState<ArrayBuffer>(null);
    const [status, setStatus] = useState<PreviewStatus>('waiting');
    const [message, setMessage] = useState<string>(null);
    const [updatedAt, setUpdatedAt] = useState<Date>(null);
    const [tokens, setTokens] = useState<IPreviewToken[]>([]);
    const [highlight, setHighlight] = useState(true);
    const [followEdits, setFollowEdits] = useState(true);
    const [zoom, setZoom] = useState<number | 'fit'>('fit');

    const latest = useRef(props);
    latest.current = props;
    const templateRef = useRef<number>(undefined);
    templateRef.current = templateId;
    const mounted = useRef(true);
    const lastSent = useRef<string>(null);
    const inFlight = useRef(false);
    const rerun = useRef(false);
    const typingTimer = useRef<number>(null);
    const blankIndex = useRef(-1);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    // Take over the pop-up's document; close it when the lease page goes away.
    useEffect(() => {
        const win = props.win;
        const doc = win.document;
        ensureStyles(doc, 'lh-live-lease-styles', LIVE_LEASE_CSS);
        const reused = doc.getElementById('lh-live-lease-root');
        if (reused) {
            reused.remove();
        }
        const root = doc.createElement('div');
        root.id = 'lh-live-lease-root';
        doc.body.appendChild(root);
        setContainer(root);

        const onUnload = () => latest.current.onClose();
        win.addEventListener('beforeunload', onUnload);
        const closedCheck = window.setInterval(() => {
            if (win.closed) {
                latest.current.onClose();
            }
        }, 700);

        return () => {
            window.clearInterval(closedCheck);
            window.clearTimeout(typingTimer.current);
            if (!win.closed) {
                win.removeEventListener('beforeunload', onUnload);
                win.close();
            }
        };
    }, [props.win]);

    useEffect(() => {
        props.win.document.title = props.title;
    }, [props.win, props.title]);

    useEffect(() => {
        previewApiFetch(`v1.0/clients/${props.clientNumber}/projects/${props.projectNumber}/documentTemplate/by-project/all`)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then((all: DocumentTemplate[]) => {
                const usable = (all || [])
                    .filter(
                        (t) =>
                            t.contractTypeReferenceNumber === LandContractType.OilAndGas &&
                            PREVIEWABLE_TYPES.indexOf(t.docType) >= 0 &&
                            t.referenceNumber > 0 &&
                            REACT_PDF_TEMPLATES.indexOf(t.referenceNumber) < 0
                    )
                    .sort((a, b) => PREVIEWABLE_TYPES.indexOf(a.docType) - PREVIEWABLE_TYPES.indexOf(b.docType) || a.name.localeCompare(b.name));
                if (!mounted.current) {
                    return;
                }
                setTemplates(usable);
                setTemplateId(usable.length > 0 ? usable[0].referenceNumber : undefined);
                if (usable.length === 0) {
                    setStatus('error');
                    setMessage('No oil and gas templates are attached to this project. Attach them under Settings, Generated Documents.');
                }
            })
            .catch(() => {
                if (mounted.current) {
                    setTemplates([]);
                    setStatus('error');
                    setMessage(`The project's templates could not be loaded.`);
                }
            });
    }, [props.clientNumber, props.projectNumber]);

    /** Posts the form to the preview endpoint when it (or the template) differs from what's on screen. */
    const refresh = useCallback(async () => {
        const docId = templateRef.current;
        if (!docId || !mounted.current) {
            return;
        }
        if (inFlight.current) {
            rerun.current = true;
            return;
        }

        let body: string;
        try {
            body = JSON.stringify(latest.current.buildPayload());
        } catch (error) {
            setStatus('error');
            setMessage(`The form couldn't be read for the preview: ${(error as Error).message}`);
            return;
        }

        const key = `${docId}|${body}`;
        if (key === lastSent.current) {
            return;
        }

        inFlight.current = true;
        setStatus('updating');
        try {
            const { clientNumber, projectNumber } = latest.current;
            const response = await previewApiFetch(`v1.0/clients/${clientNumber}/projects/${projectNumber}/documentTemplate/preview/lease/${docId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body,
            });
            // A failed payload isn't retried until the form changes; a dropped connection is retried by the poll.
            lastSent.current = key;
            if (!mounted.current) {
                return;
            }
            if (response.ok) {
                const data = await response.arrayBuffer();
                if (mounted.current && templateRef.current === docId) {
                    setDocData(data);
                    setStatus('ready');
                    setMessage(null);
                    setUpdatedAt(new Date());
                }
            } else {
                const text = await previewErrorMessage(response, `The preview could not be built (${response.status}).`);
                if (mounted.current) {
                    setStatus('error');
                    setMessage(text);
                }
            }
        } catch {
            lastSent.current = null;
            if (mounted.current) {
                setStatus('error');
                setMessage('LandHawk could not be reached to refresh the preview. Retrying...');
            }
        } finally {
            inFlight.current = false;
            if (rerun.current) {
                rerun.current = false;
                refresh();
            }
        }
    }, []);

    useImperativeHandle(
        ref,
        () => ({
            notifyChange: () => {
                window.clearTimeout(typingTimer.current);
                typingTimer.current = window.setTimeout(refresh, TYPING_PAUSE_MS);
            },
        }),
        [refresh]
    );

    // Not every change raises an input event (programmatic setValue, dialogs that save and reload the lease),
    // so also compare the form against what's on screen every POLL_MS.
    useEffect(() => {
        const poll = window.setInterval(refresh, POLL_MS);
        return () => window.clearInterval(poll);
    }, [refresh]);

    useEffect(() => {
        if (templateId) {
            refresh();
        }
    }, [templateId, refresh]);

    const describeToken = useCallback((token: string, kind: PreviewTokenKind) => {
        if (kind === 'unfilled') {
            return `${displayToken(token)} is not a LandHawk token, so it prints exactly as typed. Check the template's spelling.`;
        }
        const name = humanizeToken(token);
        const target = leasePreviewTarget(token);
        if (!target) {
            return name;
        }
        if (target.field) {
            return kind === 'empty' ? `${name} is blank. Click to fill it in.` : `${name}. Click to edit it on the form.`;
        }
        return `${name} comes from ${target.source}. Click to go there.`;
    }, []);

    const onTokenClick = (token: string, kind: PreviewTokenKind) => {
        if (kind === 'unfilled') {
            setMessage(`${displayToken(token)} isn't filled in by LandHawk and will print as typed. Fix it in the template library.`);
            return;
        }
        latest.current.onTokenClick(token);
    };

    const blanks = tokens.filter((t) => t.kind === 'empty' || t.kind === 'unfilled');
    const showNextBlank = () => {
        if (blanks.length === 0) {
            return;
        }
        blankIndex.current = (blankIndex.current + 1) % blanks.length;
        const element = blanks[blankIndex.current].element;
        element.scrollIntoView({ block: 'center', behavior: 'smooth' });
        element.classList.remove('lh-token--changed');
        // Force a reflow so the pulse restarts when the same element is pointed at twice in a row.
        element.getBoundingClientRect();
        element.classList.add('lh-token--changed');
    };

    const onTemplateChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        blankIndex.current = -1;
        setTemplateId(parseInt(event.target.value, 10));
    };

    const zoomBy = (step: number) => {
        const current = typeof zoom === 'number' ? zoom : 1;
        setZoom(Math.min(2, Math.max(0.4, Math.round((current + step) * 10) / 10)));
    };

    if (!container) {
        return null;
    }

    const statusText =
        status === 'updating'
            ? 'Updating...'
            : status === 'ready' && updatedAt
            ? `Up to date, ${updatedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' })}`
            : status === 'error'
            ? 'Not updated'
            : 'Waiting for the form...';

    return ReactDOM.createPortal(
        <div className="llw">
            <header className="llw-bar">
                <strong className="llw-title">Live Lease</strong>
                <select className="llw-select" value={templateId || ''} onChange={onTemplateChange} disabled={!templates || templates.length === 0}>
                    {!templates && <option value="">Loading templates...</option>}
                    {(templates || []).map((t) => (
                        <option key={t.id} value={t.referenceNumber}>
                            {t.docType === 'Lease Template' ? '' : `${t.docType}: `}
                            {t.name}
                        </option>
                    ))}
                </select>
                <span className={`llw-status llw-status--${status}`}>{statusText}</span>
                <span className="llw-spacer" />
                <label className="llw-toggle">
                    <input type="checkbox" checked={highlight} onChange={(e) => setHighlight(e.target.checked)} /> Highlight
                </label>
                <label className="llw-toggle" title="Scroll to what changed when it's off screen">
                    <input type="checkbox" checked={followEdits} onChange={(e) => setFollowEdits(e.target.checked)} /> Follow edits
                </label>
                <span className="llw-zoom">
                    <button type="button" onClick={() => zoomBy(-0.1)} title="Zoom out">
                        -
                    </button>
                    <button type="button" onClick={() => setZoom('fit')} className={zoom === 'fit' ? 'llw-active' : ''} title="Fit the page width">
                        Fit
                    </button>
                    <button type="button" onClick={() => zoomBy(0.1)} title="Zoom in">
                        +
                    </button>
                </span>
                <button
                    type="button"
                    className="llw-blanks"
                    onClick={showNextBlank}
                    disabled={blanks.length === 0}
                    title="Show the next blank or unfilled spot"
                >
                    {blanks.length === 0 ? 'No blanks' : `${blanks.length} blank${blanks.length === 1 ? '' : 's'}: next`}
                </button>
            </header>
            {message && <div className={`llw-banner llw-banner--${status === 'error' ? 'error' : 'info'}`}>{message}</div>}
            <div className="llw-note">Shows unsaved changes. Click a highlighted value to jump to it on the form. Page breaks are approximate.</div>
            {docData ? (
                <DocxPreviewPane
                    style={{ flex: 1 }}
                    data={docData}
                    mode="merged"
                    highlight={highlight}
                    followChanges={followEdits}
                    zoom={zoom}
                    describeToken={describeToken}
                    onTokenClick={onTokenClick}
                    onRendered={setTokens}
                    onRenderError={(text) => setMessage(`The document could not be displayed: ${text}`)}
                />
            ) : (
                <div className="llw-empty">{status === 'error' ? '' : 'Building the document...'}</div>
            )}
        </div>,
        container
    );
});

const LIVE_LEASE_CSS = `
html, body { margin: 0; height: 100%; }
body { font: 13px/1.4 "Segoe UI", system-ui, -apple-system, sans-serif; color: #1f2933; background: #d9dde3; }
#lh-live-lease-root, .llw { height: 100%; }
.llw { display: flex; flex-direction: column; }
.llw-bar { display: flex; align-items: center; gap: 8px; padding: 8px 12px; background: #6393c1; color: #fff; flex-wrap: wrap; }
.llw-title { font-size: 15px; margin-right: 4px; }
.llw-select { max-width: 320px; padding: 3px 6px; border-radius: 4px; border: 1px solid #c7d4e2; font: inherit; }
.llw-status { padding: 2px 8px; border-radius: 10px; background: rgba(255, 255, 255, 0.2); white-space: nowrap; }
.llw-status--updating { background: rgba(255, 255, 255, 0.35); }
.llw-status--error { background: #b42318; }
.llw-spacer { flex: 1; }
.llw-toggle { display: inline-flex; align-items: center; gap: 4px; cursor: pointer; white-space: nowrap; }
.llw-zoom { display: inline-flex; }
.llw-zoom button, .llw-blanks { font: inherit; padding: 2px 8px; border: 1px solid rgba(255, 255, 255, 0.6); background: transparent; color: #fff; cursor: pointer; }
.llw-zoom button:first-child { border-radius: 4px 0 0 4px; }
.llw-zoom button:last-child { border-radius: 0 4px 4px 0; }
.llw-zoom button + button { border-left: none; }
.llw-zoom .llw-active { background: rgba(255, 255, 255, 0.25); }
.llw-blanks { border-radius: 4px; white-space: nowrap; }
.llw-blanks:disabled { opacity: 0.6; cursor: default; }
.llw-banner { padding: 6px 12px; }
.llw-banner--info { background: #fff4ce; color: #5c4400; }
.llw-banner--error { background: #fde8e7; color: #8c1d18; }
.llw-note { padding: 4px 12px; font-size: 12px; color: #52606d; background: #eef1f5; border-bottom: 1px solid #d3d9e0; }
.llw-empty { flex: 1; display: flex; align-items: center; justify-content: center; color: #52606d; }
`;

export default LiveLeaseWindow;



// WEBPACK FOOTER //
// ./src/components/project/sections/LeasingComponents/livePreview/LiveLeaseWindow.tsx